import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase, practitioners, cases, invoices, payments as paymentsApi } from '../supabaseClient';
import { useAuth } from './AuthContext';

const DataContext = createContext();

export const useData = () => useContext(DataContext);

export const DataProvider = ({ children }) => {
    const { user } = useAuth();

    // State for data
    const [clients, setClients] = useState([]);
    const [orders, setOrders] = useState([]);
    const [invoicesData, setInvoices] = useState([]);
    const [payments, setPayments] = useState([]);
    const [creditNotes, setCreditNotes] = useState([]);
    const [products, setProducts] = useState([]);
    const [loadingData, setLoadingData] = useState(true);

    // Initial Load
    useEffect(() => {
        if (user) {
            fetchAllData();
        } else {
            // Reset data on logout
            setClients([]);
            setOrders([]);
            setInvoices([]);
            setPayments([]);
            setCreditNotes([]);
            setLoadingData(false);
        }
    }, [user]);

    // Helper to calculate invoice items and total amount for an order
    const calculateInvoiceDataForOrder = (order, productsList = []) => {
        const items = [];
        let total = 0;

        if (order && order.teethConfig) {
            if (Array.isArray(order.teethConfig)) {
                order.teethConfig.forEach(line => {
                    const { productId, teeth, details } = line;
                    const product = productsList.find(p => p.id === productId);

                    if (product) {
                        let priceKey = 'priceClassique';
                        if (order.gamme === 'paris') priceKey = 'priceParis';
                        if (order.gamme === 'eco') priceKey = 'priceEco';

                        const unitPrice = parseFloat(product[priceKey] ?? product.priceClassique ?? 0);
                        const quantity = line.quantity !== undefined && line.quantity !== null && !isNaN(Number(line.quantity)) 
                            ? Number(line.quantity) 
                            : (teeth.length || 1);
                        const itemPrice = unitPrice * quantity;
                        const toothLabel = teeth.map(t => t === 'HAUT' ? 'Haut' : t === 'BAS' ? 'Bas' : t).join(', ');
                        let description = `${product.name}`;

                        if (details && details.teinte) {
                            description += `, Teinte ${details.teinte}`;
                        }

                        items.push({ toothId: toothLabel, description, quantity, unitPrice, price: itemPrice, category: product.category });
                        total += itemPrice;
                    }
                });
            } else {
                Object.entries(order.teethConfig).forEach(([toothId, config]) => {
                    const { productId, details } = config;
                    const product = productsList.find(p => p.id === productId);

                    if (product) {
                        let priceKey = 'priceClassique';
                        if (order.gamme === 'paris') priceKey = 'priceParis';
                        if (order.gamme === 'eco') priceKey = 'priceEco';

                        const unitPrice = parseFloat(product[priceKey] ?? product.priceClassique ?? 0);
                        const itemPrice = unitPrice;
                        let description = `${product.name}`;

                        if (details && details.teinte) {
                            description += `, Teinte ${details.teinte}`;
                        }

                        items.push({ toothId, description, quantity: 1, unitPrice, price: itemPrice, category: product.category });
                        total += itemPrice;
                    }
                });
            }
        }

        return { items, amount: total };
    };

    // Transform snake_case from Supabase to camelCase for React components
    const transformCaseData = (caseItem) => {
        let statusHistory = [];
        if (Array.isArray(caseItem.teeth_data)) {
            statusHistory = caseItem.teeth_data;
        } else if (caseItem.status_history && Array.isArray(caseItem.status_history)) {
            statusHistory = caseItem.status_history;
        } else if (caseItem.created_at) {
            statusHistory = [{ status: caseItem.status || 'reçu', date: caseItem.created_at }];
            if (caseItem.delivery_date) {
                statusHistory.push({ status: 'Livré', date: caseItem.delivery_date });
            }
            if (caseItem.invoiced_at) {
                statusHistory.push({ status: 'Facturé', date: caseItem.invoiced_at });
            }
        }

        return {
            ...caseItem,
            id: caseItem.id,
            caseNumber: caseItem.case_number,
            patientName: caseItem.patient_name,
            patientReference: caseItem.patient_reference,
            practitionerName: caseItem.practitioner_name,
            appointmentDate: caseItem.appointment_date,
            deliveryDate: caseItem.delivery_date,
            teethConfig: caseItem.teeth_config,
            gamme: caseItem.gamme,
            clientId: caseItem.client_id,
            invoiceId: caseItem.invoice_id,
            status: caseItem.status,
            createdAt: caseItem.created_at,
            updatedAt: caseItem.updated_at,
            notes: caseItem.notes,
            workType: caseItem.work_type,
            ficheUrl: (() => {
                if (caseItem.work_type && typeof caseItem.work_type === 'string') {
                    const wt = caseItem.work_type.trim();
                    if (wt.startsWith('[') || wt.startsWith('{')) {
                        try {
                            const parsed = JSON.parse(wt);
                            const list = Array.isArray(parsed) ? parsed : [parsed];
                            return list[0]?.url || (typeof list[0] === 'string' ? list[0] : null);
                        } catch (_) {}
                    } else if (wt.startsWith('http')) {
                        return wt;
                    }
                }
                return caseItem.fiche_url || null;
            })(),
            fiches: (() => {
                let fichesList = [];
                if (caseItem.work_type && typeof caseItem.work_type === 'string') {
                    const wt = caseItem.work_type.trim();
                    if (wt.startsWith('[') || wt.startsWith('{')) {
                        try {
                            const parsed = JSON.parse(wt);
                            const list = Array.isArray(parsed) ? parsed : [parsed];
                            fichesList = list.map((item, idx) => {
                                if (typeof item === 'string') {
                                    const fileName = item.split('/').pop().split('?')[0];
                                    return {
                                        id: `fiche-${idx}`,
                                        name: decodeURIComponent(fileName).replace(/^fiche_\d+(?:_[a-z0-9]+)?_/i, '') || `Document ${idx + 1}`,
                                        fileName: fileName,
                                        url: item,
                                        storagePath: `orders/${caseItem.id}/${fileName}`,
                                        uploadedAt: null
                                    };
                                }
                                return item;
                            });
                        } catch (e) {
                            console.error("Error parsing work_type fiches JSON:", e);
                        }
                    } else if (wt.startsWith('http')) {
                        const fileName = wt.split('/').pop().split('?')[0];
                        fichesList = [{
                            id: `fiche-0`,
                            name: decodeURIComponent(fileName).replace(/^fiche_\d+(?:_[a-z0-9]+)?_/i, '') || 'Fiche reçue',
                            fileName: fileName,
                            url: wt,
                            storagePath: `orders/${caseItem.id}/${fileName}`,
                            uploadedAt: caseItem.updated_at || caseItem.created_at
                        }];
                    }
                }
                if (fichesList.length === 0 && caseItem.fiche_url) {
                    const fileName = caseItem.fiche_url.split('/').pop().split('?')[0];
                    fichesList = [{
                        id: `fiche-0`,
                        name: decodeURIComponent(fileName).replace(/^fiche_\d+(?:_[a-z0-9]+)?_/i, '') || 'Fiche reçue',
                        fileName: fileName,
                        url: caseItem.fiche_url,
                        storagePath: `orders/${caseItem.id}/${fileName}`,
                        uploadedAt: caseItem.updated_at || caseItem.created_at
                    }];
                }
                return fichesList;
            })(),
            invoicedAt: caseItem.invoiced_at,
            statusHistory: statusHistory,
            internalNotes: caseItem.internal_notes || (() => {
                try {
                    return localStorage.getItem(`eno_memo_${caseItem.id}`) || '';
                } catch (_) {
                    return '';
                }
            })(),
        };
    };

    // Transform invoice data from snake_case to camelCase
    const transformInvoiceData = (inv) => ({
        ...inv,
        id: inv.id,
        invoiceNumber: inv.invoice_number,
        orderId: inv.order_id,
        clientId: inv.client_id,
        patientName: inv.patient_name,
        practitionerName: inv.practitioner_name,
        items: inv.items,
        amount: inv.amount || inv.total_amount,
        totalAmount: inv.total_amount,
        date: inv.date,
        status: inv.status,
        createdAt: inv.created_at,
    });

    const fetchAllData = async () => {
        try {
            setLoadingData(true);
            const [
                { data: practData },
                { data: caseData },
                { data: invData },
                { data: payData },
                { data: creditData },
                { data: prodData, error: prodErr }
            ] = await Promise.all([
                supabase.from('practitioners').select('*'),
                supabase.from('cases').select('*'),
                supabase.from('invoices').select('*'),
                supabase.from('payments').select('*'),
                // Safe wrapper to prevent whole fetch failure if credit_notes table/RLS isn't fully ready
                (async () => {
                    try {
                        return await supabase.from('credit_notes').select('*');
                    } catch (e) {
                        console.warn("Table credit_notes could not be loaded, using fallback.", e);
                        return { data: null };
                    }
                })(),
                (async () => {
                    try {
                        return await supabase.from('products').select('*');
                    } catch (e) {
                        console.warn("Table products could not be loaded from DB, using local fallback.", e);
                        return { data: null, error: e };
                    }
                })()
            ]);

            if (practData) setClients(practData);
            // Transform case data from snake_case to camelCase
            if (caseData) setOrders(caseData.map(transformCaseData));
            // Transform invoice data from snake_case to camelCase
            if (invData) setInvoices(invData.map(transformInvoiceData));

            // Separate regular payments from cloud-persisted credit notes (stored in payments with payment_type = 'Avoir' / 'Avoir_Pending')
            const rawPayments = payData || [];
            const regularPayments = rawPayments.filter(p => {
                const pt = (p.payment_type || p.type || '').trim().toLowerCase();
                return pt !== 'avoir' && pt !== 'avoir_pending';
            });
            const paymentBackedCredits = rawPayments.filter(p => {
                const pt = (p.payment_type || p.type || '').trim().toLowerCase();
                return pt === 'avoir' || pt === 'avoir_pending';
            }).map(p => ({
                id: p.id,
                clientId: p.client_id,
                invoiceId: p.invoice_id,
                creditNoteNumber: p.notes || `AV-${String(p.id).substring(0, 6).toUpperCase()}`,
                date: p.date || p.payment_date || p.created_at || new Date().toISOString(),
                amount: parseFloat(p.amount || 0),
                reference: p.reference || '-',
                status: (p.payment_type || '').trim().toLowerCase() === 'avoir_pending' ? 'pending' : 'approved',
                createdAt: p.created_at || p.date || new Date().toISOString(),
                _fromPayments: true
            }));

            setPayments(regularPayments);

            // Transform credit notes from credit_notes table + payment-backed credit notes
            const tableCredits = (creditData || []).map(c => ({
                id: c.id,
                clientId: c.client_id,
                invoiceId: c.invoice_id,
                creditNoteNumber: c.credit_note_number,
                date: c.date || c.credit_date || new Date().toISOString().split('T')[0],
                amount: parseFloat(c.amount || 0),
                reference: c.reason || c.notes || '-',
                status: c.status || 'approved',
                createdAt: c.created_at || new Date().toISOString()
            }));

            const mergedCloudCredits = [...tableCredits, ...paymentBackedCredits];
            if (mergedCloudCredits.length > 0) {
                setCreditNotes(mergedCloudCredits);
                localStorage.setItem('enovadent_credit_notes', JSON.stringify(mergedCloudCredits));
            } else {
                const savedCreditNotes = localStorage.getItem('enovadent_credit_notes');
                if (savedCreditNotes) {
                    try {
                        setCreditNotes(JSON.parse(savedCreditNotes));
                    } catch (e) {
                        setCreditNotes([]);
                    }
                } else {
                    setCreditNotes([]);
                }
            }

            // Products - Sync with Supabase DB or fallback to localStorage
            if (prodData && prodData.length > 0) {
                const transformedProducts = prodData.map(p => ({
                    id: p.id,
                    category: p.category,
                    name: p.name,
                    priceClassique: parseFloat(p.price_classique ?? p.priceClassique ?? 0),
                    priceParis: parseFloat(p.price_paris ?? p.priceParis ?? p.price_classique ?? 0),
                    priceEco: parseFloat(p.price_eco ?? p.priceEco ?? 0),
                    description: p.description || ''
                }));
                setProducts(transformedProducts);
                localStorage.setItem('enovadent_products', JSON.stringify(transformedProducts));
            } else {
                const currentVersion = '2026-05-30-v2';
                const savedVersion = localStorage.getItem('enovadent_catalog_version');
                const savedProducts = localStorage.getItem('enovadent_products');
                let initialProds = [];
                
                if (savedVersion !== currentVersion) {
                    initialProds = getDefaultProducts();
                    localStorage.setItem('enovadent_catalog_version', currentVersion);
                } else if (savedProducts) {
                    try { initialProds = JSON.parse(savedProducts); } catch (e) { initialProds = getDefaultProducts(); }
                } else {
                    initialProds = getDefaultProducts();
                }

                setProducts(initialProds);
                localStorage.setItem('enovadent_products', JSON.stringify(initialProds));

                // Auto-seed Supabase DB whenever products table in DB is empty
                if (!prodData || prodData.length === 0) {
                    try {
                        const seedPayload = initialProds.map(p => ({
                            id: String(p.id),
                            category: p.category,
                            name: p.name,
                            price_classique: p.priceClassique || 0,
                            price_paris: p.priceParis || p.priceClassique || 0
                        }));
                        await supabase.from('products').upsert(seedPayload);
                    } catch (seedErr) {
                        console.warn("Auto-seeding products to DB failed:", seedErr);
                    }
                }
            }

            // Auto-heal missing invoices for cases marked 'Facturé' or 'Facture'
            const currentCases = caseData ? caseData.map(transformCaseData) : [];
            const currentInvoices = invData ? invData.map(transformInvoiceData) : [];

            const missingInvoiceCases = currentCases.filter(c => {
                const isFacture = c.status && (c.status.toLowerCase() === 'facturé' || c.status.toLowerCase() === 'facture');
                if (!isFacture) return false;
                const hasInv = currentInvoices.some(inv => inv.orderId === c.id || inv.order_id === c.id || c.invoiceId === inv.id);
                return !hasInv;
            });

            if (missingInvoiceCases.length > 0) {
                const activeProducts = (prodData && prodData.length > 0) ? prodData.map(p => ({
                    id: p.id,
                    category: p.category,
                    name: p.name,
                    priceClassique: parseFloat(p.price_classique ?? p.priceClassique ?? 0),
                    priceParis: parseFloat(p.price_paris ?? p.priceParis ?? p.price_classique ?? 0),
                    priceEco: parseFloat(p.price_eco ?? p.priceEco ?? 0)
                })) : getDefaultProducts();

                for (const c of missingInvoiceCases) {
                    try {
                        const { items: calcItems, amount: calcAmount } = calculateInvoiceDataForOrder(c, activeProducts);
                        const generatedInvNum = c.caseNumber || (typeof c.id === 'string' ? c.id.substring(0, 8).toUpperCase() : `ENO-${Date.now()}`);

                        const invPayload = {
                            invoice_number: generatedInvNum,
                            order_id: c.id,
                            client_id: c.clientId || null,
                            patient_name: c.patientName || null,
                            practitioner_name: c.practitionerName || null,
                            items: calcItems,
                            amount: calcAmount,
                            total_amount: calcAmount,
                            date: c.invoicedAt || c.createdAt || new Date().toISOString(),
                            status: 'Emise',
                            created_at: new Date().toISOString()
                        };

                        const { data: invRes, error: invErr } = await supabase.from('invoices').insert([invPayload]).select();
                        if (!invErr && invRes && invRes.length > 0) {
                            const newInv = transformInvoiceData(invRes[0]);
                            currentInvoices.push(newInv);
                            await supabase.from('cases').update({ invoice_id: newInv.id, invoiced_at: newInv.date }).eq('id', c.id);
                        }
                    } catch (e) {
                        console.warn("Auto-healing missing invoice failed for case:", c.id, e);
                    }
                }
                setInvoices(currentInvoices);
            }

            setLoadingData(false);
        } catch (error) {
            console.error("Error fetching data:", error);
            setLoadingData(false);
        }
    };

    // --- Actions (Wrapped with Supabase calls) ---

    // CLIENTS
    const addClient = async (client) => {
        try {
            const { data, error } = await supabase.from('practitioners').insert([client]).select();
            if (error) throw error;
            setClients(prev => [...prev, ...data]);
            return data[0];
        } catch (error) {
            console.error("Error adding client:", error);
            alert("Erreur lors de l'ajout du praticien");
        }
    };

    const updateClient = async (id, updates) => {
        try {
            const { error } = await supabase.from('practitioners').update(updates).eq('id', id);
            if (error) throw error;
            setClients(prev => prev.map(c => c.id === id ? { ...c, ...updates } : c));
        } catch (error) {
            console.error("Error updating client:", error);
        }
    };

    const deleteClient = async (id) => {
        try {
            const { error } = await supabase.from('practitioners').delete().eq('id', id);
            if (error) {
                if (error.code === '23503') {
                    throw new Error("Impossible de supprimer ce praticien car il est lié à des travaux, des factures, des règlements ou un compte utilisateur. Vous devez d'abord supprimer ces éléments associés pour pouvoir le retirer.");
                }
                throw error;
            }
            setClients(prev => prev.filter(c => c.id !== id));
            alert("Praticien supprimé avec succès.");
        } catch (error) {
            console.error("Error deleting client:", error);
            alert(error.message || "Une erreur est survenue lors de la suppression du praticien.");
        }
    };

    // ORDERS
    const addOrder = async (order) => {
        try {
            // Remove local ID if present to let DB auto-gen UUID
            // But we need to handle if the UI expects immediate ID return
            // Mapping camelCase to snake_case for Supabase
            const { id, ...orderData } = order;
            // Generate Sequential Case Number ENOXX
            // Find max ENO number in existing orders
            let maxOrderNum = 0;
            orders.forEach(o => {
                const match = (o.caseNumber || '').match(/ENO-(\d+)/i);
                if (match) {
                    const num = parseInt(match[1], 10);
                    if (num > maxOrderNum) maxOrderNum = num;
                }
            });
            const nextOrderNum = maxOrderNum + 1;
            const caseNumber = `ENO-${nextOrderNum.toString().padStart(2, '0')}`;

            const now = new Date().toISOString();
            const initialStatus = orderData.status || 'reçu';
            const initialHistory = [{ status: initialStatus, date: now }];

            // Ensure status is lowercase 'reçu' as per default or user inputs
            const payload = {
                case_number: caseNumber,
                status: initialStatus,
                // Explicit mapping to avoid "column not found" errors
                client_id: orderData.clientId || null,
                patient_name: orderData.patientName || null,
                patient_reference: orderData.patientReference || null,
                practitioner_name: orderData.practitionerName || null,
                // Convert empty string to null for DATE type
                appointment_date: orderData.appointmentDate ? orderData.appointmentDate : null,
                teeth_config: orderData.teethConfig || null,
                teeth_data: initialHistory,
                gamme: orderData.gamme || 'classique',
                notes: orderData.notes || null,
                work_type: orderData.fiches
                    ? (Array.isArray(orderData.fiches) ? JSON.stringify(orderData.fiches) : orderData.fiches)
                    : (orderData.ficheUrl || null),
                created_at: orderData.createdAt || now,
                updated_at: now
            };
            const { data, error } = await supabase.from('cases').insert([payload]).select();
            if (error) throw error;
            const transformedOrder = transformCaseData(data[0]);
            setOrders(prev => [transformedOrder, ...prev]);
            return transformedOrder;
        } catch (error) {
            console.error("Error adding order:", error);
            throw error;
        }
    };

    /**
     * Supprime automatiquement les fichiers 3D (STL) d'un cas du stockage cloud Supabase
     * pour libérer l'espace et respecter les quotas une fois le travail facturé.
     * @param {string} caseId 
     * @returns {Promise<{success: boolean, deletedCount: number}>}
     */
    const cleanupCaseSTLFiles = async (caseId) => {
        if (!caseId) return { success: false, deletedCount: 0 };
        try {
            // 1. Lister les fichiers dans le dossier orders/${caseId} du bucket 'stl-files'
            const { data: storageFiles, error: listErr } = await supabase.storage
                .from('stl-files')
                .list(`orders/${caseId}`, { limit: 100 });

            let deletedCount = 0;
            if (storageFiles && storageFiles.length > 0) {
                const filesToRemove = storageFiles
                    .filter(f => f.name && f.id !== null)
                    .map(f => `orders/${caseId}/${f.name}`);

                if (filesToRemove.length > 0) {
                    const { error: removeErr } = await supabase.storage
                        .from('stl-files')
                        .remove(filesToRemove);

                    if (!removeErr) {
                        deletedCount = filesToRemove.length;
                        console.log(`[STL Cleanup] ${deletedCount} fichier(s) STL purgé(s) du cloud pour le cas ${caseId}`);
                    } else {
                        console.warn(`[STL Cleanup] Erreur suppression storage pour cas ${caseId}:`, removeErr);
                    }
                }
            }

            // 2. Vider également la liste des fichiers dans la table case_transfers
            try {
                await supabase
                    .from('case_transfers')
                    .update({ files: [], updated_at: new Date().toISOString() })
                    .eq('case_id', caseId);
            } catch (tErr) {
                // Table case_transfers optionnelle
            }

            return { success: true, deletedCount };
        } catch (err) {
            console.error(`[STL Cleanup] Erreur purge STL pour cas ${caseId}:`, err);
            return { success: false, deletedCount: 0, error: err };
        }
    };

    /**
     * Purge globale de tous les fichiers STL pour l'ensemble des cas déjà au statut 'Facturé'
     */
    const purgeAllInvoicedSTLFiles = async () => {
        const invoicedOrders = (orders || []).filter(o => 
            o.status && (o.status.toLowerCase() === 'facturé' || o.status.toLowerCase() === 'facture')
        );

        let totalPurgedFiles = 0;
        let totalPurgedCases = 0;

        for (const ord of invoicedOrders) {
            try {
                const res = await cleanupCaseSTLFiles(ord.id);
                if (res.deletedCount > 0) {
                    totalPurgedFiles += res.deletedCount;
                    totalPurgedCases++;
                }
            } catch (e) {
                console.warn(`[STL Purge] Erreur sur le cas ${ord.id}:`, e);
            }
        }

        return { totalPurgedCases, totalPurgedFiles, totalInvoicedScanned: invoicedOrders.length };
    };

    const updateOrder = async (id, updates) => {
        try {
            const now = new Date().toISOString();
            // Map updates to snake_case for Supabase
            const payload = {};
            if (updates.patientName !== undefined) payload.patient_name = updates.patientName;
            if (updates.patientReference !== undefined) payload.patient_reference = updates.patientReference;
            if (updates.practitionerName !== undefined) payload.practitioner_name = updates.practitionerName;
            if (updates.appointmentDate !== undefined) payload.appointment_date = updates.appointmentDate;
            if (updates.gamme !== undefined) payload.gamme = updates.gamme;
            if (updates.teethConfig !== undefined) payload.teeth_config = updates.teethConfig;
            if (updates.clientId !== undefined) payload.client_id = updates.clientId;
            if (updates.invoiceId !== undefined) payload.invoice_id = updates.invoiceId;
            if (updates.invoicedAt !== undefined) payload.invoiced_at = updates.invoicedAt;
            if (updates.deliveryDate !== undefined) payload.delivery_date = updates.deliveryDate;
            if (updates.notes !== undefined) payload.notes = updates.notes;
            if (updates.internalNotes !== undefined) {
                payload.internal_notes = updates.internalNotes;
                try {
                    if (updates.internalNotes) {
                        localStorage.setItem(`eno_memo_${id}`, updates.internalNotes);
                    } else {
                        localStorage.removeItem(`eno_memo_${id}`);
                    }
                } catch (_) {}
            }
            if (updates.fiches !== undefined) {
                payload.work_type = Array.isArray(updates.fiches) ? JSON.stringify(updates.fiches) : updates.fiches;
            } else if (updates.ficheUrl !== undefined) {
                payload.work_type = updates.ficheUrl;
            }
            if (updates.priority !== undefined) payload.priority = updates.priority;

            // Handle status change and history tracking
            if (updates.status !== undefined) {
                payload.status = updates.status;

                const existingOrder = orders.find(o => o.id === id);
                const prevHistory = (existingOrder && Array.isArray(existingOrder.statusHistory))
                    ? existingOrder.statusHistory
                    : (existingOrder && Array.isArray(existingOrder.teeth_data) ? existingOrder.teeth_data : []);

                const newHistory = [
                    ...prevHistory,
                    { status: updates.status, date: now }
                ];
                payload.teeth_data = newHistory;
                updates.statusHistory = newHistory;

                const sLower = updates.status.toLowerCase();
                if (sLower === 'livré' || sLower === 'livre') {
                    payload.delivery_date = now;
                    updates.deliveryDate = now;
                }
            }

            // If status is being updated to 'Facturé', automatically generate invoice if missing
            if (updates.status && (updates.status.toLowerCase() === 'facturé' || updates.status.toLowerCase() === 'facture')) {
                const existingOrder = orders.find(o => o.id === id);
                const targetOrder = { ...(existingOrder || {}), ...updates };

                const existingInv = invoicesData.find(inv => 
                    (inv.orderId && inv.orderId === id) || 
                    (inv.order_id && inv.order_id === id) || 
                    (targetOrder.invoiceId && inv.id === targetOrder.invoiceId)
                );

                if (!existingInv && !updates.invoiceId) {
                    const { items: calcItems, amount: calcAmount } = calculateInvoiceDataForOrder(targetOrder, products);
                    const generatedInvNum = targetOrder.caseNumber || (typeof id === 'string' ? id.substring(0, 8).toUpperCase() : `ENO-${Date.now()}`);
                    // Preserve original invoicedAt date if the case was previously billed so it stays in its original billing month
                    const invoiceDate = targetOrder.invoicedAt || existingOrder?.invoicedAt || now;

                    try {
                        const createdInv = await addInvoice({
                            invoiceNumber: generatedInvNum,
                            orderId: id,
                            clientId: targetOrder.clientId || null,
                            patientName: targetOrder.patientName || '',
                            practitionerName: targetOrder.practitionerName || '',
                            items: calcItems,
                            amount: calcAmount,
                            date: invoiceDate,
                            status: 'Emise'
                        });

                        if (createdInv && createdInv.id) {
                            payload.invoice_id = createdInv.id;
                            payload.invoiced_at = createdInv.date || invoiceDate;
                            updates.invoiceId = createdInv.id;
                            updates.invoicedAt = payload.invoiced_at;
                        }
                    } catch (invErr) {
                        console.error("Auto-generating invoice on status change failed:", invErr);
                    }
                } else if (existingInv) {
                    payload.invoiced_at = existingInv.date || targetOrder.invoicedAt || now;
                    updates.invoicedAt = payload.invoiced_at;
                }

                // Nettoyage automatique des fichiers 3D (STL) du cloud pour économiser le stockage
                cleanupCaseSTLFiles(id).catch(err => console.warn("[STL Cleanup] Nettoyage auto échoué:", err));
            }

            // Only update if there's something to update
            if (Object.keys(payload).length === 0) {
                console.warn("updateOrder called with empty payload");
                return;
            }

            payload.updated_at = now;
            let { error } = await supabase.from('cases').update(payload).eq('id', id);
            if (error && (error.code === 'PGRST204' || error.code === '42703' || (error.message && error.message.includes('internal_notes'))) && payload.internal_notes !== undefined) {
                // Column internal_notes is not yet added in Supabase SQL editor; save rest to DB and keep local memo
                const safePayload = { ...payload };
                delete safePayload.internal_notes;
                if (Object.keys(safePayload).length > 1) { // more than just updated_at
                    const fbRes = await supabase.from('cases').update(safePayload).eq('id', id);
                    error = fbRes.error;
                } else {
                    error = null;
                }
            }
            if (error) throw error;
            setOrders(prev => prev.map(o => o.id === id ? { ...o, ...updates, ...(payload.teeth_data ? { statusHistory: payload.teeth_data } : {}), ...(payload.delivery_date ? { deliveryDate: payload.delivery_date } : {}) } : o));
        } catch (error) {
            console.error("Error updating order:", error);
            throw error;
        }
    };

    const deleteOrder = async (id) => {
        try {
            // Nettoyage des fichiers 3D associés avant suppression
            cleanupCaseSTLFiles(id).catch(err => console.warn("[STL Cleanup] Purge suppression échouée:", err));
            const { error } = await supabase.from('cases').delete().eq('id', id);
            if (error) throw error;
            setOrders(prev => prev.filter(o => o.id !== id));
        } catch (error) {
            console.error("Error deleting order:", error);
            throw error;
        }
    };

    // INVOICES
    const addInvoice = async (invoice) => {
        try {
            // Use provided invoice number or generate Sequential Invoice Number ENO-XX
            let invoiceNumber = invoice.invoiceNumber || invoice.invoice_number;
            if (!invoiceNumber) {
                let maxInvNum = 0;
                invoicesData.forEach(inv => {
                    const match = (inv.invoiceNumber || inv.invoice_number || '').match(/ENO-(\d+)/i);
                    if (match) {
                        const num = parseInt(match[1], 10);
                        if (num > maxInvNum) maxInvNum = num;
                    }
                });
                const nextInvNum = maxInvNum + 1;
                invoiceNumber = `ENO-${nextInvNum.toString().padStart(2, '0')}`;
            }

            // Anti-double-billing guard: check if an invoice already exists for this orderId (or same invoiceNumber for same client)
            const existingForOrder = invoicesData.find(inv =>
                (invoice.orderId && (inv.orderId === invoice.orderId || inv.order_id === invoice.orderId)) ||
                (invoiceNumber && (inv.invoiceNumber || inv.invoice_number) === invoiceNumber && (inv.clientId || inv.client_id) === (invoice.clientId || null))
            );

            if (existingForOrder) {
                // If already paid, do not overwrite its date or amount (use a credit note for adjustments instead)
                if (existingForOrder.status === 'Payée') {
                    console.warn(`[Anti-Double-Facturation] La facture ${invoiceNumber} est déjà réglée (Payée). Aucun doublon créé.`);
                    return existingForOrder;
                }
                const updatePayload = {
                    client_id: invoice.clientId || existingForOrder.clientId || existingForOrder.client_id || null,
                    patient_name: invoice.patientName || existingForOrder.patientName || existingForOrder.patient_name || null,
                    practitioner_name: invoice.practitionerName || existingForOrder.practitionerName || existingForOrder.practitioner_name || null,
                    items: invoice.items || existingForOrder.items || null,
                    amount: invoice.amount ?? existingForOrder.amount ?? 0,
                    total_amount: invoice.amount ?? existingForOrder.amount ?? 0,
                    // Preserve original invoice date so it doesn't shift into another billing month
                    date: existingForOrder.date || invoice.date || new Date().toISOString()
                };
                const { data: updData, error: updErr } = await supabase
                    .from('invoices')
                    .update(updatePayload)
                    .eq('id', existingForOrder.id)
                    .select();
                if (updErr) throw updErr;
                const transformedUpdated = updData && updData[0]
                    ? transformInvoiceData(updData[0])
                    : { ...existingForOrder, ...invoice, date: updatePayload.date };
                setInvoices(prev => prev.map(inv => inv.id === existingForOrder.id ? transformedUpdated : inv));
                return transformedUpdated;
            }

            // Map camelCase to snake_case for Supabase
            const payload = {
                invoice_number: invoiceNumber,
                order_id: invoice.orderId || null,
                client_id: invoice.clientId || null,
                patient_name: invoice.patientName || null,
                practitioner_name: invoice.practitionerName || null,
                items: invoice.items || null,
                amount: invoice.amount || 0,
                total_amount: invoice.amount || 0,
                date: invoice.date || new Date().toISOString(),
                status: invoice.status || 'Emise',
                created_at: new Date().toISOString()
            };
            const { data, error } = await supabase.from('invoices').insert([payload]).select();
            if (error) throw error;
            const transformedInvoice = transformInvoiceData(data[0]);
            setInvoices(prev => [...prev, transformedInvoice]);
            return transformedInvoice;
        } catch (error) {
            console.error("Error adding invoice:", error);
            throw error;
        }
    };

    const updateInvoice = async (id, updates) => {
        try {
            const { error } = await supabase.from('invoices').update(updates).eq('id', id);
            if (error) throw error;
            setInvoices(prev => prev.map(inv => inv.id === id ? { ...inv, ...updates } : inv));
        } catch (error) {
            console.error("Error updating invoice:", error);
        }
    };

    const deleteInvoice = async (id) => {
        try {
            // 1. Delete invoice
            const { error } = await supabase.from('invoices').delete().eq('id', id);
            if (error) throw error;
            setInvoices(prev => prev.filter(inv => inv.id !== id));

            // 2. Unlink orders (locally and remotely)
            // Ideally should be a DB trigger or stored procedure, but doing client-side for now
            const linkedOrders = orders.filter(o => o.invoiceId === id);
            for (const order of linkedOrders) {
                await updateOrder(order.id, { invoiceId: null, status: order.status === 'Facturé' ? 'Livré' : order.status });
            }
        } catch (error) {
            console.error("Error deleting invoice:", error);
        }
    };

    // PAYMENTS
    const addPayment = async (payment) => {
        try {
            // Map camelCase to snake_case for Supabase
            const payload = {
                client_id: payment.clientId || null,
                invoice_id: payment.invoiceId || null,
                amount: payment.amount || 0,
                date: payment.date || new Date().toISOString(),
                payment_type: payment.type || payment.paymentType || 'Espèces',
                reference: payment.reference || null,
                notes: payment.notes || null,
                created_at: new Date().toISOString()
            };
            const { data, error } = await supabase.from('payments').insert([payload]).select();
            if (error) throw error;
            setPayments(prev => [...prev, ...data]);
            return data[0];
        } catch (error) {
            console.error("Error adding payment:", error);
            throw error;
        }
    };

    const deletePayment = async (id) => {
        try {
            const { error } = await supabase.from('payments').delete().eq('id', id);
            if (error) throw error;
            setPayments(prev => prev.filter(p => p.id !== id));
        } catch (error) {
            console.error("Error deleting order:", error);
            throw error;
        }
    };

    // CREDIT NOTES (Supabase integrated via payments table for 100% cloud persistence + local cache)
    const addCreditNote = async (note) => {
        const creditNum = note.creditNoteNumber || `AV-${Date.now().toString().substr(5, 5)}`;
        const isoDate = note.date ? new Date(note.date).toISOString() : new Date().toISOString();
        const statusVal = note.status || 'approved';

        try {
            // Persist in payments table with payment_type = 'Avoir' or 'Avoir_Pending' so RLS never blocks it
            const paymentPayload = {
                client_id: note.clientId || null,
                invoice_id: note.invoiceId || null,
                amount: parseFloat(note.amount || 0),
                date: isoDate,
                payment_type: statusVal === 'pending' ? 'Avoir_Pending' : 'Avoir',
                reference: note.reference || note.reason || 'Avoir',
                notes: creditNum,
                created_at: new Date().toISOString()
            };

            const { data, error } = await supabase.from('payments').insert([paymentPayload]).select();
            if (error) throw error;

            if (data && data.length > 0) {
                const row = data[0];
                const transformed = {
                    id: row.id,
                    clientId: row.client_id,
                    invoiceId: row.invoice_id,
                    creditNoteNumber: row.notes || creditNum,
                    date: row.date || isoDate,
                    amount: parseFloat(row.amount || 0),
                    reference: row.reference || '-',
                    status: (row.payment_type || '').toLowerCase() === 'avoir_pending' ? 'pending' : 'approved',
                    createdAt: row.created_at || new Date().toISOString(),
                    _fromPayments: true
                };

                setCreditNotes(prev => {
                    const newNotes = [...prev, transformed];
                    localStorage.setItem('enovadent_credit_notes', JSON.stringify(newNotes));
                    return newNotes;
                });
                return transformed;
            }
        } catch (error) {
            console.error("Error adding credit note in Supabase, falling back to local:", error);
            const localNote = {
                ...note,
                id: Date.now().toString(),
                createdAt: new Date().toISOString(),
                date: note.date || new Date().toISOString().split('T')[0],
                creditNoteNumber: creditNum,
                status: statusVal
            };
            setCreditNotes(prev => {
                const newNotes = [...prev, localNote];
                localStorage.setItem('enovadent_credit_notes', JSON.stringify(newNotes));
                return newNotes;
            });
            return localNote;
        }
    };

    const updateCreditNote = async (id, updates) => {
        try {
            const targetNote = creditNotes.find(n => n.id === id);
            if (targetNote && targetNote._fromPayments) {
                const payUpdates = {};
                if (updates.status !== undefined) {
                    payUpdates.payment_type = updates.status === 'pending' ? 'Avoir_Pending' : 'Avoir';
                }
                if (updates.reason !== undefined || updates.reference !== undefined) {
                    payUpdates.reference = updates.reference || updates.reason;
                }
                if (updates.amount !== undefined) {
                    payUpdates.amount = parseFloat(updates.amount || 0);
                }
                const { error } = await supabase.from('payments').update(payUpdates).eq('id', id);
                if (error) throw error;
            } else {
                const payload = {};
                if (updates.status !== undefined) payload.status = updates.status;
                if (updates.reason !== undefined) payload.reason = updates.reason;
                if (updates.amount !== undefined) payload.amount = updates.amount;

                const { error } = await supabase.from('credit_notes').update(payload).eq('id', id);
                if (error) throw error;
            }

            setCreditNotes(prev => {
                const newNotes = prev.map(n => n.id === id ? { ...n, ...updates } : n);
                localStorage.setItem('enovadent_credit_notes', JSON.stringify(newNotes));
                return newNotes;
            });
        } catch (error) {
            console.error("Error updating credit note in Supabase, falling back to local:", error);
            setCreditNotes(prev => {
                const newNotes = prev.map(n => n.id === id ? { ...n, ...updates } : n);
                localStorage.setItem('enovadent_credit_notes', JSON.stringify(newNotes));
                return newNotes;
            });
        }
    };

    const deleteCreditNote = async (id) => {
        try {
            const targetNote = creditNotes.find(n => n.id === id);
            if (targetNote && targetNote._fromPayments) {
                const { error } = await supabase.from('payments').delete().eq('id', id);
                if (error) throw error;
            } else {
                const { error } = await supabase.from('credit_notes').delete().eq('id', id);
                if (error) {
                    await supabase.from('payments').delete().eq('id', id);
                }
            }

            setCreditNotes(prev => {
                const newNotes = prev.filter(n => n.id !== id);
                localStorage.setItem('enovadent_credit_notes', JSON.stringify(newNotes));
                return newNotes;
            });
        } catch (error) {
            console.error("Error deleting credit note in Supabase, falling back to local:", error);
            setCreditNotes(prev => {
                const newNotes = prev.filter(n => n.id !== id);
                localStorage.setItem('enovadent_credit_notes', JSON.stringify(newNotes));
                return newNotes;
            });
        }
    };

    // PRODUCTS (Synced with Supabase DB + local cache)
    const addProduct = async (product) => {
        const newProduct = {
            id: product.id || `p_${Date.now()}`,
            category: product.category,
            name: product.name,
            priceClassique: parseFloat(product.priceClassique || 0),
            priceParis: parseFloat(product.priceParis || product.priceClassique || 0),
            description: product.description || ''
        };

        setProducts(prev => {
            const newProds = [...prev, newProduct];
            localStorage.setItem('enovadent_products', JSON.stringify(newProds));
            return newProds;
        });

        try {
            const payload = {
                id: newProduct.id,
                category: newProduct.category,
                name: newProduct.name,
                price_classique: newProduct.priceClassique,
                price_paris: newProduct.priceParis,
                description: newProduct.description
            };
            await supabase.from('products').upsert([payload]);
        } catch (err) {
            console.warn("Error syncing added product to Supabase DB:", err);
        }
    };

    const updateProduct = async (id, updates) => {
        setProducts(prev => {
            const newProds = prev.map(p => p.id === id ? { ...p, ...updates } : p);
            localStorage.setItem('enovadent_products', JSON.stringify(newProds));
            return newProds;
        });

        try {
            const payload = {};
            if (updates.category !== undefined) payload.category = updates.category;
            if (updates.name !== undefined) payload.name = updates.name;
            if (updates.priceClassique !== undefined) payload.price_classique = updates.priceClassique;
            if (updates.priceParis !== undefined) payload.price_paris = updates.priceParis;
            if (updates.description !== undefined) payload.description = updates.description;

            await supabase.from('products').update(payload).eq('id', id);
        } catch (err) {
            console.warn("Error updating product in Supabase DB:", err);
        }
    };

    const deleteProduct = async (id) => {
        setProducts(prev => {
            const newProds = prev.filter(p => p.id !== id);
            localStorage.setItem('enovadent_products', JSON.stringify(newProds));
            return newProds;
        });

        try {
            await supabase.from('products').delete().eq('id', id);
        } catch (err) {
            console.warn("Error deleting product in Supabase DB:", err);
        }
    };

    const resetProducts = async () => {
        if (window.confirm("Voulez-vous vraiment réinitialiser le catalogue ?")) {
            const defaults = getDefaultProducts();
            setProducts(defaults);
            localStorage.setItem('enovadent_products', JSON.stringify(defaults));

            try {
                const seedPayload = defaults.map(p => ({
                    id: String(p.id),
                    category: p.category,
                    name: p.name,
                    price_classique: p.priceClassique || 0,
                    price_paris: p.priceParis || p.priceClassique || 0
                }));
                await supabase.from('products').upsert(seedPayload);
            } catch (err) {
                console.warn("Error resetting products in Supabase DB:", err);
            }
        }
    };


    const getClientAccountingSummary = (clientId, clientName) => {
        const normName = (clientName || '').trim().toLowerCase();
        const matchesName = (val) => normName && (val || '').trim().toLowerCase() === normName;

        // Match invoices by clientId OR practitionerName (for cases where clientId wasn't set)
        const clientInvoices = invoicesData.filter(inv =>
            (clientId && (inv.clientId === clientId || inv.client_id === clientId)) ||
            matchesName(inv.practitionerName) ||
            matchesName(inv.practitioner_name) ||
            matchesName(inv.clientName)
        );

        // Match payments by clientId OR clientName
        const clientPayments = payments.filter(p =>
            (clientId && (p.clientId === clientId || p.client_id === clientId)) ||
            matchesName(p.clientName)
        );

        // Match credit notes by clientId OR clientName (only approved credit notes reduce balance)
        const clientCreditNotes = creditNotes.filter(n =>
            ((clientId && (n.clientId === clientId || n.client_id === clientId)) ||
            matchesName(n.clientName)) && n.status !== 'pending'
        );

        const totalBilled = clientInvoices.reduce((sum, inv) => sum + (parseFloat(inv.amount || inv.total_amount || inv.total || 0) || 0), 0);
        const totalPaid = clientPayments.reduce((sum, p) => {
            const amt = parseFloat(p.amount || 0);
            const isRefund = (p.payment_type || p.type || '').toLowerCase().includes('remboursement');
            return sum + (isRefund ? -amt : amt);
        }, 0);
        const totalCredits = clientCreditNotes.reduce((sum, n) => sum + (parseFloat(n.amount) || 0), 0);

        const balance = totalBilled - (totalPaid + totalCredits);

        return {
            totalBilled,
            totalPaid,
            totalCredits,
            balance,
            lastPaymentDate: clientPayments.length > 0 ? clientPayments[clientPayments.length - 1].date : null
        };
    };

    const value = {
        clients,
        addClient,
        updateClient,
        deleteClient,
        orders,
        addOrder,
        updateOrder,
        deleteOrder,
        invoices: invoicesData,
        addInvoice,
        updateInvoice,
        deleteInvoice,
        payments,
        addPayment,
        deletePayment,
        creditNotes,
        addCreditNote,
        updateCreditNote,
        deleteCreditNote,
        products,
        addProduct,
        updateProduct,
        deleteProduct,
        resetProducts,
        cleanupCaseSTLFiles,
        purgeAllInvoicedSTLFiles,
        getClientAccountingSummary,
        loadingData // Expose loading state
    };

    return (
        <DataContext.Provider value={value}>
            {children}
        </DataContext.Provider>
    );
};

// Helper: Default Products List
const getDefaultProducts = () => [
    // INLAY CORE ET COURONNE COULÉE
    { id: 'p_couronne_coulee', category: 'INLAY CORE ET COURONNE COULÉE', name: "Couronne coulée CoCr", priceClassique: 55, priceParis: 55 },
    { id: 'p34', category: 'INLAY CORE ET COURONNE COULÉE', name: "Inlay core métal CoCr", priceClassique: 30, priceParis: 30 },
    { id: 'p_ic_opaque', category: 'INLAY CORE ET COURONNE COULÉE', name: "inlay core métal CoCr opaque", priceClassique: 32, priceParis: 32 },
    { id: 'p_clavette', category: 'INLAY CORE ET COURONNE COULÉE', name: "clavette", priceClassique: 9, priceParis: 9 },
    { id: 'p35', category: 'INLAY CORE ET COURONNE COULÉE', name: "Inlay Core Claveté CoCr", priceClassique: 39, priceParis: 39 },

    // CCM ET DENTS PROVISOIRES
    { id: 'p27', category: 'CCM ET DENTS PROVISOIRES', name: "Céramique sur métal CoCr", priceClassique: 69, priceParis: 69 },
    { id: 'p28', category: 'CCM ET DENTS PROVISOIRES', name: "Richmond sur métal CoCr non précieux", priceClassique: 75, priceParis: 75 },
    { id: 'p29', category: 'CCM ET DENTS PROVISOIRES', name: "Armature CoCr", priceClassique: 38, priceParis: 38 },
    { id: 'p30', category: 'CCM ET DENTS PROVISOIRES', name: "Finition céramique sur CCM et Richmond", priceClassique: 37, priceParis: 37 },
    { id: 'p31', category: 'CCM ET DENTS PROVISOIRES', name: "Épaulement céramique", priceClassique: 22, priceParis: 22 },
    { id: 'p_ceram_rose', category: 'CCM ET DENTS PROVISOIRES', name: "céramique rose", priceClassique: 24, priceParis: 24 },
    { id: 'p32', category: 'CCM ET DENTS PROVISOIRES', name: "Dent provisoire", priceClassique: 15, priceParis: 15 },
    { id: 'p33', category: 'CCM ET DENTS PROVISOIRES', name: "Inlay / Onlay Composite", priceClassique: 50, priceParis: 50 },

    // TOUT CÉRAMIQUE, CFAO
    { id: 'p43', category: 'TOUT CÉRAMIQUE, CFAO', name: "Couronne Emax/Facette Emax", priceClassique: 91, priceParis: 91 },
    { id: 'p44', category: 'TOUT CÉRAMIQUE, CFAO', name: "Zircone Stratifiée", priceClassique: 88, priceParis: 88 },
    { id: 'p45', category: 'TOUT CÉRAMIQUE, CFAO', name: "Full Zircone Monochrome", priceClassique: 57, priceParis: 57 },
    { id: 'p46', category: 'TOUT CÉRAMIQUE, CFAO', name: "Full Zircone Multilayer compact", priceClassique: 62, priceParis: 62 },
    { id: 'p_fz_multi_trans', category: 'TOUT CÉRAMIQUE, CFAO', name: "Full zircone Multi Translucent", priceClassique: 64, priceParis: 64 },
    { id: 'p47', category: 'TOUT CÉRAMIQUE, CFAO', name: "Armature Zircone", priceClassique: 62, priceParis: 62 },
    { id: 'p48', category: 'TOUT CÉRAMIQUE, CFAO', name: "Finition Céramique sur Zircone", priceClassique: 30, priceParis: 30 },
    { id: 'p49', category: 'TOUT CÉRAMIQUE, CFAO', name: "Model 3D arcade complète", priceClassique: 10, priceParis: 10 },
    { id: 'p_prep_appareil', category: 'TOUT CÉRAMIQUE, CFAO', name: "préparation sous appareil", priceClassique: 12, priceParis: 12 },
    { id: 'p_prep_transv', category: 'TOUT CÉRAMIQUE, CFAO', name: "Préparation complèt transvissée", priceClassique: 50, priceParis: 50 },

    // IMPLANT
    { id: 'p_pilier_cfao', category: 'IMPLANT', name: "Pilier CFAO personnalisé", priceClassique: 105, priceParis: 105 },
    { id: 'p_vis_def', category: 'IMPLANT', name: "VIS définitive (patient)", priceClassique: 22, priceParis: 22 },
    { id: 'p_fz_mono_scelle', category: 'IMPLANT', name: "Full zircone monochrome scellé", priceClassique: 120, priceParis: 120 },
    { id: 'p_fz_mono_trans', category: 'IMPLANT', name: "Full zircone monochrome transvissée", priceClassique: 125, priceParis: 125 },
    { id: 'p_fz_multi_scelle', category: 'IMPLANT', name: "Full zircone multi compact scellé", priceClassique: 123, priceParis: 123 },
    { id: 'p_fz_multi_trans_vis', category: 'IMPLANT', name: "Full zircone multi compact transvissée", priceClassique: 127, priceParis: 127 },
    { id: 'p_fz_multi_tr_scelle', category: 'IMPLANT', name: "Full zircone multi translucent scellé", priceClassique: 125, priceParis: 125 },
    { id: 'p_fz_multi_tr_trans', category: 'IMPLANT', name: "Full zircone multi translucent transfixée", priceClassique: 129, priceParis: 129 },
    { id: 'p36', category: 'IMPLANT', name: "Finition Céramique sur implant scellée", priceClassique: 47, priceParis: 47 },
    { id: 'p37', category: 'IMPLANT', name: "Armature Céramo-Métallique sur Implant Transvissée", priceClassique: 95, priceParis: 95 },
    { id: 'p38', category: 'IMPLANT', name: "Finition Céramique sur implant Transvissée", priceClassique: 49, priceParis: 49 },
    { id: 'p_zirc_strat_scelle', category: 'IMPLANT', name: "Zircone stratifiée scellée sur implant", priceClassique: 145, priceParis: 145 },
    { id: 'p_zirc_strat_trans', category: 'IMPLANT', name: "Zircone stratifiée Transvissée sur implant", priceClassique: 155, priceParis: 155 },
    { id: 'p41', category: 'IMPLANT', name: "Faux Moignon Implantaire Calcinable", priceClassique: 65, priceParis: 65 },
    { id: 'p42', category: 'IMPLANT', name: "Préparation / Fraisage de Pilier Implantaire", priceClassique: 15, priceParis: 15 },
    { id: 'p_emax_uscelle', category: 'IMPLANT', name: "Emax usinée scellée", priceClassique: 150, priceParis: 150 },
    { id: 'p_emax_utrans', category: 'IMPLANT', name: "Emax usinée transvissée", priceClassique: 155, priceParis: 155 },
    { id: 'p_barre_all4', category: 'IMPLANT', name: "Barre titane all on 4", priceClassique: 508, priceParis: 508 },
    { id: 'p_barre_all4_res', category: 'IMPLANT', name: "Barre titane all on 4 + complet résine transvissée", priceClassique: 1400, priceParis: 1400 },
    { id: 'p_barre_all6', category: 'IMPLANT', name: "Barre titane all on 6", priceClassique: 762, priceParis: 762 },
    { id: 'p_barre_all6_res', category: 'IMPLANT', name: "Barre titane all on 6 + complet résine transvissée", priceClassique: 1750, priceParis: 1750 },

    // ATTACHEMENT
    { id: 'p_pos_attache', category: 'ATTACHEMENT', name: "Positionnement dʼattachement", priceClassique: 31, priceParis: 31 },
    { id: 'p_attache_boule', category: 'ATTACHEMENT', name: "Attachement boule", priceClassique: 64, priceParis: 64 },
    { id: 'p_attache_asc', category: 'ATTACHEMENT', name: "Attachement ASC 52", priceClassique: 66, priceParis: 66 },
    { id: 'p_locator', category: 'ATTACHEMENT', name: "Locator", priceClassique: 70, priceParis: 70 },
    { id: 'p_gaine_rempl', category: 'ATTACHEMENT', name: "gaine de remplacement", priceClassique: 55, priceParis: 55 },

    // DIVERS
    { id: 'p1', category: 'DIVERS', name: "Cire d'occlusion", priceClassique: 16, priceParis: 16 },
    { id: 'p2', category: 'DIVERS', name: "Cire d'occlusion + P.E.I", priceClassique: 35, priceParis: 35 },
    { id: 'p3', category: 'DIVERS', name: "Porte empreinte", priceClassique: 22, priceParis: 22 },
    { id: 'p4', category: 'DIVERS', name: "Rebasage", priceClassique: 49, priceParis: 49 },

    // GOUTTIÈRES
    { id: 'p5', category: 'GOUTTIÈRES', name: "Gouttière de Bruxisme souple", priceClassique: 55, priceParis: 55 },
    { id: 'p6', category: 'GOUTTIÈRES', name: "Gouttière de Bruxisme dure", priceClassique: 55, priceParis: 55 },
    { id: 'p7', category: 'GOUTTIÈRES', name: "Gouttière de Blanchiment", priceClassique: 41, priceParis: 41 },

    // PROTHÈSE PARTIELLE
    { id: 'p8', category: 'PROTHÈSE PARTIELLE', name: "Prothèse 1 à 5 dents (essayage cire)", priceClassique: 56, priceParis: 56 },
    { id: 'p9', category: 'PROTHÈSE PARTIELLE', name: "Prothèse 1 à 5 dents (finition directe)", priceClassique: 70, priceParis: 70 },
    { id: 'p10', category: 'PROTHÈSE PARTIELLE', name: "Prothèse 6 à 13 dents (essayage cire)", priceClassique: 69, priceParis: 69 },
    { id: 'p11', category: 'PROTHÈSE PARTIELLE', name: "Prothèse 6 à 13 dents (finition directe)", priceClassique: 83, priceParis: 83 },

    // PROTHÈSE COMPLÈTE
    { id: 'p12', category: 'PROTHÈSE COMPLÈTE', name: "Prothèse complète (essayage cire)", priceClassique: 89, priceParis: 89 },
    { id: 'p13', category: 'PROTHÈSE COMPLÈTE', name: "Prothèse complète (finition directe)", priceClassique: 103, priceParis: 103 },
    { id: 'p_pc_res_usine', category: 'PROTHÈSE COMPLÈTE', name: "Prothèse complète résine usiné", priceClassique: 190, priceParis: 190 },
    { id: 'p14', category: 'PROTHÈSE COMPLÈTE', name: "Finition prothèse résine", priceClassique: 35, priceParis: 35 },

    // STELLITE
    { id: 'p15', category: 'STELLITE', name: "Stellite CoCr finition directe", priceClassique: 124, priceParis: 124 },
    { id: 'p16', category: 'STELLITE', name: "Stellite plaque nue CoCr", priceClassique: 82, priceParis: 82 },
    { id: 'p17', category: 'STELLITE', name: "Stellite Acétal finition directe", priceClassique: 93, priceParis: 93 },
    { id: 'p18', category: 'STELLITE', name: "Finition Stellite", priceClassique: 42, priceParis: 42 },
    { id: 'p19', category: 'STELLITE', name: "Supplément par dent", priceClassique: 4, priceParis: 4 },

    // RÉPARATION, FINITION
    { id: 'p20', category: 'REPARATION, FINITION', name: "Grille et renfort pour appareil résine", priceClassique: 45, priceParis: 45 },
    { id: 'p21', category: 'REPARATION, FINITION', name: "Retouche cire terminée", priceClassique: 36, priceParis: 36 },
    { id: 'p22', category: 'REPARATION, FINITION', name: "Modification de teinte", priceClassique: 25, priceParis: 25 },
    { id: 'p23', category: 'REPARATION, FINITION', name: "Réparation appareil mobile", priceClassique: 40, priceParis: 40 },
    { id: 'p24', category: 'REPARATION, FINITION', name: "Adjonction dent sur appareil résine", priceClassique: 29, priceParis: 29 },
    { id: 'p25', category: 'REPARATION, FINITION', name: "Adjonction dent sur stellite", priceClassique: 39, priceParis: 39 },
    { id: 'p26', category: 'REPARATION, FINITION', name: "Adjonction crochet fil façonné", priceClassique: 19, priceParis: 19 }
];
